import * as THREE from 'three';

import { Animation, AnimationConfig } from './types';

export class TubeAnimation3D implements Animation {
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private mesh: THREE.Mesh;
  private group: THREE.Group;
  private rotationSpeed: number = 0.1;

  constructor({ canvas, size }: AnimationConfig) {
    // Adjust camera FOV and position for smaller scene
    this.camera = new THREE.PerspectiveCamera(50, 1, 1, 1000);
    this.camera.position.z = 35;

    // Scene setup
    this.scene = new THREE.Scene();
    this.group = new THREE.Group();
    this.scene.add(this.group);

    // Reduce tube dimensions
    const length = 15;
    const radius = 3;
    const pi2 = Math.PI * 2;

    // Create custom curve for the tube
    const curve = new THREE.CatmullRomCurve3();
    curve.getPoint = (percent: number): THREE.Vector3 => {
      const x = length * Math.sin(pi2 * percent);
      const y = radius * Math.cos(pi2 * 3 * percent);
      let t = (percent % 0.25) / 0.25;
      t = (percent % 0.25) - (2 * (1 - t) * t * -0.0185 + t * t * 0.25);
      if (
        Math.floor(percent / 0.25) === 0 ||
        Math.floor(percent / 0.25) === 2
      ) {
        t *= -1;
      }
      const z = radius * Math.sin(pi2 * 2 * (percent - t));
      return new THREE.Vector3(x, y, z);
    };

    // Reduce tube thickness
    const geometry = new THREE.TubeGeometry(curve, 200, 0.6, 2, true);
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff });
    this.mesh = new THREE.Mesh(geometry, material);
    this.group.add(this.mesh);

    // Renderer setup
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(size, size);
    this.renderer.setClearColor(0x000000, 0);

    // Adjust group position
    this.group.position.z = -3;

    // Reduce initial scale
    this.mesh.scale.set(0.7, 0.7, 0.7);
  }

  update(): void {
    // Simple constant rotation animation
    this.mesh.rotation.x += this.rotationSpeed;
    this.renderer.render(this.scene, this.camera);
  }

  cleanup(): void {
    // Clean up Three.js resources
    this.renderer.dispose();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
